import { version as reactVersion } from 'react';
import { createTracker } from 'usage-tracker-public';
import events from '../../events.yaml';
import Raven from 'raven-js';
import { Metrics } from 'payments-ui-app/Metrics';
import { getIframeLayout, getReferrer } from './queryParams';
import { getLineItemRecurringFrequency } from './recurring';
import { INTERACTION_SECTIONS } from '../constants/PageInteractionTracking';
import { isOptional } from '../types/utils/typeguards';
import { trackSubscriptionCreation } from './subscriptionTracking';
import keyBy from 'hs-lodash/keyBy';
import { isFutureStartDateBillingTerms, isRelativeStartDateBillingTerms } from '../types/utils/typeguards';
import { METRICS_JS_COUNTERS } from '../constants/Metrics';
export const PAYMENT_SCHEDULE_MARKER = 'PaymentScheduleInstallment';
let tracker;
let beaconTracker;
export function createUuid() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    let v;
    // eslint-disable-next-line no-bitwise
    const r = Math.random() * 16 | 0;
    if (c === 'x') {
      v = r;
    } else {
      // eslint-disable-next-line no-bitwise
      v = r & 0x3 | 0x8;
    }
    return v.toString(16);
  });
}
export function initializePaymentsTracker({
  portalId,
  sourceUrl,
  workflow,
  merchantSupportEmail
}) {
  tracker = createTracker({
    events,
    properties: {
      namespace: 'paymentsui',
      hstc: null,
      hubId: portalId,
      email: null,
      sourceUrl,
      workflow,
      merchantSupportEmail,
      referrer: getReferrer(),
      deviceId: createUuid()
    },
    onError: err => {
      Raven.captureMessage('payments-ui tracker event error', {
        extra: {
          message: err.message
        }
      });
    }
  });
  beaconTracker = tracker.clone({});
}
export function track(event, eventProps = {}) {
  if (!tracker) {
    throw new Error('Tracker has not been initialized');
  }
  tracker.track(event, Object.assign({}, eventProps, {
    screen: getIframeLayout() ? 'embedded' : 'public'
  }));
}
export function trackImmediately(event, eventProps = {}) {
  if (!beaconTracker) {
    throw new Error('Beacon tracker has not been initialized');
  }
  beaconTracker.track(event, Object.assign({}, eventProps, {
    screen: getIframeLayout() ? 'embedded' : 'public'
  }));
}
function getEventPropsFromFormState(formState) {
  const paymentMethod = formState.get('paymentMethod');
  const customFormFields = Object.keys(formState.get('customData').toJS());
  if (formState.get('discountCode')) {
    customFormFields.push('discount_code');
  }
  const collectShippingAddress = formState.get('shippingAddress').some(shippingAddressField => Boolean(shippingAddressField));
  const isBillingSameAsShipping = formState.get('billingSameAsShipping');
  return {
    paymentMethod,
    customFormFields,
    collectShippingAddress,
    isBillingSameAsShipping
  };
}
function getSalesTaxAndFeePropsFromAdjustments(adjustments) {
  let hasSalesTax = false;
  let hasOneTimeFees = false;
  for (const adjustment of adjustments) {
    if (hasSalesTax && hasOneTimeFees) {
      return {
        hasSalesTax,
        hasOneTimeFees
      };
    }
    if (adjustment.adjustmentType === 'TAX') {
      hasSalesTax = true;
    } else if (adjustment.adjustmentType === 'FEE') {
      hasOneTimeFees = true;
    }
  }
  return {
    hasSalesTax,
    hasOneTimeFees
  };
}
export function getSalesTaxType(paymentSession) {
  if (paymentSession.get('sourceApp') === 'SALES_CHECKOUT' && paymentSession.get('isAutomatedSalesTaxEnabled') || paymentSession.get('commerceTaxCalculationId')) {
    return 'AUTO';
  }
  const {
    hasSalesTax
  } = getSalesTaxAndFeePropsFromAdjustments(paymentSession.get('adjustments').toJS());
  return hasSalesTax ? 'MANUAL' : 'NONE';
}
function getUniqueBillingFrequencyArray(recurringLineItems) {
  const uniqueBillingFrequencies = new Set();
  recurringLineItems === null || recurringLineItems === void 0 || recurringLineItems.forEach(lineItem => {
    const billingFrequency = getLineItemRecurringFrequency(lineItem);
    if (billingFrequency) {
      uniqueBillingFrequencies.add(billingFrequency);
    }
  });
  return Array.from(uniqueBillingFrequencies);
}
function getEventPropsFromPaymentSession(paymentSession) {
  const processorType = paymentSession.get('processorType');
  const currencyCode = paymentSession.get('currencyCode');
  const numLineItems = paymentSession.get('lineItems').size;
  const numProductLineItems = paymentSession.get('lineItems').filter(lineItem => Boolean(lineItem.get('sourceId'))).size;
  const numCustomLineItems = numLineItems - numProductLineItems;
  const numOneTimeLineItems = paymentSession.get('lineItems').filter(lineItem => !lineItem.get('recurringBillingTerms')).size;
  const numRecurringLineItems = numLineItems - numOneTimeLineItems;
  const numLineItemsEditable = paymentSession.get('lineItems').filter(lineItem => !!lineItem.getIn(['customConfiguration', 'hasEditableQuantity'])).size;
  const numLineItemsOpenAmount = paymentSession.get('lineItems').filter(lineItem => !!lineItem.getIn(['customConfiguration', 'hasEditablePrice'])).size;
  const numLineItemsWithTax = paymentSession.get('lineItems').filter(lineItem => !!lineItem.get('tax')).size;
  const requiredCheckout = !paymentSession.get('lineItems').some(lineItem => !!lineItem.getIn(['customConfiguration', 'isOptional']));
  const paymentScheduleEnabled = paymentSession.get('lineItems').some(lineItem => {
    const sourceId = lineItem.get('sourceId');
    return !!sourceId && sourceId.includes(PAYMENT_SCHEDULE_MARKER);
  });
  const billingFrequency = getUniqueBillingFrequencyArray(paymentSession.get('lineItems').filter(lineItem => Boolean(lineItem.get('recurringBillingTerms'))).toJS());
  const {
    hasOneTimeFees
  } = getSalesTaxAndFeePropsFromAdjustments(paymentSession.get('adjustments').toJS());
  const salesTax = getSalesTaxType(paymentSession);
  const hasSuccessUrl = Boolean(paymentSession.get('successUrl'));
  const sourceApp = paymentSession.get('sourceApp');
  const paymentType = paymentSession.get('processorType');
  const sourceId = paymentSession.get('sourceId');
  const hasDescriptionHtml = Boolean(paymentSession.get('descriptionHtml'));
  const lineItems = paymentSession.get('lineItems').toJS();
  const numLineItemsScheduled = lineItems.filter(li => Boolean(li.recurringBillingTerms && (isFutureStartDateBillingTerms(li.recurringBillingTerms) || isRelativeStartDateBillingTerms(li.recurringBillingTerms)))).length;
  const isPaymentMethodOnFileEnabled = paymentSession.get('collectPaymentMethodOnFile');
  return {
    currencyCode,
    numLineItems,
    numLineItemsEditable,
    numLineItemsOpenAmount,
    numLineItemsWithTax,
    numLineItemsScheduled,
    requiredCheckout,
    numProductLineItems,
    numCustomLineItems,
    numOneTimeLineItems,
    numRecurringLineItems,
    billingFrequency,
    hasSuccessUrl,
    paymentSessionId: paymentSession.get('id'),
    processorType,
    sourceApp,
    sourceId,
    hasDescriptionHtml,
    salesTax,
    oneTimeFeesAmount: hasOneTimeFees,
    paymentType,
    paymentScheduleEnabled,
    isPaymentMethodOnFileEnabled
  };
}
function getEventPropsFromBuyerOverridesLineItems(buyerOverridesLineItems, checkoutSessionLineItems) {
  const overridesById = keyBy(buyerOverridesLineItems, 'id');
  return checkoutSessionLineItems.reduce((acc, l) => {
    const optional = l.customConfiguration && isOptional(l);
    if (optional) ++acc.numLineItemsOptional;
    if (!(l.id in overridesById)) return acc;
    if (optional) ++acc.numLineItemsOptionalSelected;
    const override = overridesById[l.id];
    if ('quantity' in override) {
      ++acc.numLineItemsEdited;
      ++acc.numLineItemsEditedQuantity;
    } else if ('price' in override) {
      ++acc.numLineItemsEdited;
      ++acc.numLineItemsEditedOpenAmount;
      ++acc.dealPipelineStageId;
    }
    return acc;
  }, {
    numLineItemsEditedOpenAmount: 0,
    numLineItemsEditedQuantity: 0,
    numLineItemsOptional: 0,
    numLineItemsOptionalSelected: 0,
    numLineItemsEdited: 0,
    dealPipelineStageId: 0
  });
}
function getOrderLevelDiscount(checkoutSession) {
  try {
    if (!checkoutSession.orderDiscount || checkoutSession.orderDiscount.discountCode) {
      return {};
    }
    const {
      valueType,
      value
    } = checkoutSession.orderDiscount;
    return {
      discountsApplied: valueType,
      totalDiscountsRate: value
    };
  } catch (e) {
    return {};
  }
}
export function trackIntentCreationAttempt(sourceApp) {
  Metrics.counter(METRICS_JS_COUNTERS.intent_creation_attempt_total, {
    screen: getIframeLayout() ? 'embedded' : 'public',
    source: sourceApp
  }).increment();
}
export function trackIntentConfirmationAttempt(sourceApp) {
  Metrics.counter(METRICS_JS_COUNTERS.intent_confirmation_attempt_total, {
    screen: getIframeLayout() ? 'embedded' : 'public',
    source: sourceApp
  }).increment();
}
export function trackCheckoutSessionLoaded() {
  Metrics.counter(METRICS_JS_COUNTERS.checkout_session_loaded, {
    screen: getIframeLayout() ? 'embedded' : 'public'
  }).increment();
}
export function trackSuccessfulPaymentSubmission(formState, paymentSession, buyerOverridesLineItems, walletType, feeType) {
  const session = paymentSession.toJS();
  Metrics.counter(METRICS_JS_COUNTERS.successful_checkout_submission).increment();
  trackImmediately('paymentsCheckoutSubmission', Object.assign({
    action: 'successfulPaymentSubmission',
    screen: getIframeLayout() ? 'embedded' : 'public'
  }, getEventPropsFromFormState(formState), getEventPropsFromPaymentSession(paymentSession), getEventPropsFromBuyerOverridesLineItems(buyerOverridesLineItems, paymentSession.get('lineItems').toJS()), getOrderLevelDiscount(session), {
    walletType,
    feeType
  }));
  trackSubscriptionCreation(session);
}
export function trackPageFormFieldFirstInteraction(section, target) {
  Metrics.counter(METRICS_JS_COUNTERS.checkout_page_interaction).increment();
  track('pageInteraction', {
    action: 'firstInteraction',
    type: 'form',
    section,
    target
  });
}
export function trackRecaptchaFailureOrBlocked() {
  track('pageInteraction', {
    action: 'recaptchaFailure'
  });
}
export function trackErrorBoundaryTriggered({
  error,
  info,
  boundaryName
}) {
  Metrics.counter(METRICS_JS_COUNTERS.error_boundary_triggered).increment();
  Raven.captureException(error, {
    extra: Object.assign({}, info, {
      version: reactVersion
    }),
    tags: {
      errorBoundaryName: boundaryName
    }
  });
}
export function trackStripePaymentElementLoadError({
  stripeErrorMessage = 'unknown',
  stripeErrorCode = 'unknown'
}) {
  Metrics.counter(METRICS_JS_COUNTERS.stripe_payment_element_onload_error, {
    stripeErrorCode
  }).increment();
  Raven.captureMessage('Stripe Payment Element onLoad Error', {
    extra: {
      stripeErrorMessage,
      stripeErrorCode
    }
  });
}
export function trackStripeConfirmIntentFailed({
  stripeErrorMessage = 'unknown',
  stripeErrorCode = 'unknown'
}) {
  Metrics.counter(METRICS_JS_COUNTERS.stripe_confirm_intent_failed, {
    stripeErrorCode
  }).increment();
  Raven.captureMessage('Stripe confirm intent failed', {
    extra: {
      stripeErrorMessage,
      stripeErrorCode
    }
  });
}
export function getPageInteractionSections(paymentSession) {
  const interactionSections = [INTERACTION_SECTIONS.contactInfo, INTERACTION_SECTIONS.billingAddress];
  try {
    const paymentOptions = paymentSession.get('paymentMethods').map(paymentMethod => paymentMethod.get('paymentMethod'));
    if (paymentSession.get('shippableCountries') && paymentSession.get('shippableCountries').size) {
      // @ts-expect-error make INTERACTION_SECTIONS an enum, and interactionSections type Array<INTERACTION_SECTIONS>;
      interactionSections.push(INTERACTION_SECTIONS.shippingAddress);
    }
    if (paymentOptions.size && paymentOptions.includes('CARD')) {
      // @ts-expect-error make INTERACTION_SECTIONS an enum, and interactionSections type Array<INTERACTION_SECTIONS>;
      interactionSections.push(INTERACTION_SECTIONS.cardInfo);
    }
    if (paymentOptions.size && paymentOptions.includes('ACH')) {
      // @ts-expect-error make INTERACTION_SECTIONS an enum, and interactionSections type Array<INTERACTION_SECTIONS>;
      interactionSections.push(INTERACTION_SECTIONS.bankInfo);
    }
    if (paymentSession.get('collectFullBillingAddress')) {
      // @ts-expect-error make INTERACTION_SECTIONS an enum, and interactionSections type Array<INTERACTION_SECTIONS>;
      interactionSections.push(INTERACTION_SECTIONS.fullBillingAddress);
    }
  } catch (error) {
    Raven.captureMessage('getPageInteractionSections error', {
      extra: {
        message: error.message
      }
    });
  }
  return interactionSections;
}